import { PropsWithChildren, memo, useEffect, useRef, useState } from 'react';

import { isNil } from 'lodash-es';
import { useMeasure } from 'react-use';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Box, ClickAwayListener, Popper, Stack } from '@mui/material';

import {
  isDrawerLockedAtom,
  isDrawerOpenAtom,
} from '@/components/shared/PrimaryNavDrawer/PrimaryNavStore';
import NotificationFeed from '@/pages/NotificationSettings/NotificationFeed';
import { useInAppNotificationFeed } from '@/providers/InAppNotificationFeedProvider';

import PrimaryNavNotification from './PrimaryNavNotification';

type PrimaryNavNotificationProps = {
  isCurrentPage: boolean;
  isDrawerOpen: boolean;
};

const BADGE_ANIMATION_DURATION_MS = 1500;

const NotificationItemWithFeedWrapper = ({
  isCurrentPage,
  isDrawerOpen,
  children,
}: PropsWithChildren<{ isCurrentPage: boolean; isDrawerOpen: boolean }>) => {
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [shouldAnimateBadge, setShouldAnimateBadge] = useState(false);

  const isDrawerLocked = useRecoilValue(isDrawerLockedAtom);
  const setIsDrawerOpen = useSetRecoilState(isDrawerOpenAtom);

  const { feedClient, unreadCount, feedAnchorEl, setFeedAnchorEl } =
    useInAppNotificationFeed();
  /**
   * By referencing the ListItemButton, its size and position updated values
   * will be used to place the Popover component accordingly when the animation ends.
   */
  const [itemButtonRef] = useMeasure<HTMLDivElement>();

  const isNotificationFeedOpen = !!feedAnchorEl;
  const hasUnreadNotifications = unreadCount > 0;
  const shouldShowCounterBadge =
    !isNotificationFeedOpen && hasUnreadNotifications;

  const handleClickAway = () => {
    setFeedAnchorEl(null);
    feedClient.markAllAsRead();

    if (isDrawerLocked || !isDrawerOpen) return;
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    const onNotificationsReceived = () => {
      if (isDrawerOpen) return;

      if (!isNil(animationTimeoutRef.current)) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }

      setShouldAnimateBadge(true);
      animationTimeoutRef.current = setTimeout(() => {
        setShouldAnimateBadge(false);
      }, BADGE_ANIMATION_DURATION_MS);
    };

    feedClient.on('items.received.realtime', onNotificationsReceived);

    return () => {
      feedClient.off('items.received.realtime', onNotificationsReceived);
    };
  }, [feedClient, isDrawerOpen]);

  return (
    <>
      <PrimaryNavNotification
        ref={itemButtonRef}
        isCurrentPage={isCurrentPage}
        isDrawerOpen={isDrawerOpen}
        Counter={
          shouldShowCounterBadge && (
            <Stack
              data-cy="notification-badge-counter"
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: '50%',
                ml: 'auto',
                fontSize: '0.75rem',
                bgcolor: (theme) => theme.palette.cyan500,
              }}
            >
              {unreadCount}
            </Stack>
          )
        }
        onItemClick={(anchor) => {
          setFeedAnchorEl(feedAnchorEl ? null : anchor);

          if (feedAnchorEl) {
            feedClient.markAllAsRead();
          }
        }}
        isItemBadgeVisible={shouldShowCounterBadge}
        unreadCount={shouldAnimateBadge ? unreadCount : undefined}
        data-cy="primary-nav-notification-with-feed"
      />
      <Popper
        id="notification-feed"
        open={isNotificationFeedOpen}
        anchorEl={feedAnchorEl}
        placement="right"
        sx={{
          zIndex: (theme) => theme.zIndex.modal,
        }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            sx={{
              boxShadow: (theme) => theme.shadows[10],
              overflow: 'hidden',
              borderRadius: 1.5,
              ml: 4,
              mb: 3,
            }}
          >
            {children}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

const PrimaryNavNotificationWithFeed = ({
  isCurrentPage,
  isDrawerOpen,
}: PrimaryNavNotificationProps) => {
  return (
    <NotificationItemWithFeedWrapper
      isCurrentPage={isCurrentPage}
      isDrawerOpen={isDrawerOpen}
    >
      <NotificationFeed />
    </NotificationItemWithFeedWrapper>
  );
};

export default memo(PrimaryNavNotificationWithFeed);
